import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { DARK_SURFACE } from './dark-tokens';
import { HOVER_LIFT, HOVER_SURFACE } from './hover-grammar';

type SurfaceBaseProps = {
  /** Restricted to safe RSC tag names — no arbitrary `ElementType` across the boundary. */
  as?: 'div' | 'section' | 'article' | 'figure';
  hover?: 'none' | 'promote' | 'lift';
  highlightTop?: boolean;
  className?: string;
  children: ReactNode;
};

export type SurfaceProps = SurfaceBaseProps &
  ({ variant?: 'flat'; mediaRole?: never } | { variant: 'elevated'; mediaRole: 'phone' | 'hero' });

/**
 * Surface — the house card recipe (R4-P2 build brief §2 + §6.1 [AMENDED b4],
 * founder Q1/Q2 LOCKED). Resting = fill `DARK_SURFACE.base` (`#161410`
 * post-R6 warm retint, was `#121418`) + 1px `#FFFFFF1A` hairline,
 * `box-shadow: none`. Radius is unconditionally `12px` — Q1 LOCKED: this is
 * the ONLY house value (RuleFlowNode owns its own 8px shell via
 * `mock-surface-recipe.ts` instead of a Surface prop, since a genuinely
 * configurable radius isn't a real requirement here — R4-P2 correctness
 * review Finding 4: an earlier `radius?: 12` prop was dead/no-op weight,
 * threaded through every call site but never read by this component;
 * deleted rather than wired up, since the type only ever permitted the one
 * literal this component already hardcodes). The heavy media shadow
 * (`0 24px 80px rgba(0,0,0,.28)`) is an opt-in `elevated` variant gated to
 * `mediaRole` (PhoneShell / hero figure only) — an ordinary card can never
 * claim it.
 *
 * No event-handler props, no `...rest`: Surface accepts only the documented,
 * serializable props so it stays a Server Component; interactive semantics
 * (click/keydown) belong in a `'use client'` wrapper one layer up.
 */
export function Surface(props: SurfaceProps) {
  const { as: Tag = 'div', hover = 'none', highlightTop = false, className, children } = props;
  const variant = props.variant ?? 'flat';
  const mediaRole = props.variant === 'elevated' ? props.mediaRole : undefined;

  return (
    <Tag
      data-primitive="Surface"
      data-media-role={mediaRole}
      data-stroke-role="card"
      data-hover-target={hover !== 'none' ? '' : undefined}
      className={cn(
        // Card border P2.5 contrast ladder v2 (§D): #FFFFFF1A (0.102) ->
        // #FFFFFF26 (0.149) -- the single house-card token; MockCard /
        // ScheduleGrid / PhoneShell inherit it via this component. Coupled
        // rubric anchors (measure-gallery.mjs c3/c7) updated in the same
        // pass.
        'relative rounded-[12px] border border-[#FFFFFF26]',
        DARK_SURFACE.base.bg,
        variant === 'flat'
          ? 'shadow-none'
          : 'shadow-[inset_0_1px_0_rgba(255,255,255,.04),0_24px_80px_rgba(0,0,0,.28)]',
        // highlightTop = a 1px top wash, layered as a second `background-image`
        // (not an inset box-shadow) so a flat Surface's computed box-shadow
        // stays `none` (Q2 LOCKED). border-radius clips it natively.
        highlightTop &&
          'bg-[linear-gradient(to_bottom,rgba(255,255,255,.04),rgba(255,255,255,.04)_1px,transparent_1px,transparent_100%)]',
        hover === 'promote' && HOVER_SURFACE,
        hover === 'lift' && HOVER_LIFT,
        className
      )}
    >
      {children}
    </Tag>
  );
}
